import { computed, observable, runInAction } from "mobx";
import i18n from "../../../constants/i18n";
import { memoize } from "lodash";
import {
  TConfigurationReceipt,
  TDataInputObject,
  TReceiptCompensationTitles,
  TCurrentMonth,
  TAdminFinalReceipts
} from "../../../Types";
import getCoreUIStore from "../../core/core-ui-store";

import {
  ADMIN_AUDITED_EVENT_COUNTS_DEFAULTS,
  ADMIN_DEFAULT_ISSUES_COUNT,
  ADMIN_EVENT_COUNTS_DEFAULTS,
  CONFIGURATION_RECEIPT_DEFAULTS,
  CURRENT_MONTH_CALCULATION_ARRAY,
  SUPERVISOR_CURRENT_MONTH_DEFAULTS,
  SUPERVISOR_SUBMIT_BUTTON_MAX_DATE_ENABLED,
  SUPERVISOR_SUBMIT_BUTTON_MIN_DATE_ENABLED
} from "../../../constants/input-entries-constants";
import { apiCallSubmitSupervisorCurrentMonthReceipt } from "../../../api/supervisor-api/api-call-submit-supervisor-current-month-receipt";
import getCorePopUpStore from "../../core/core-popup-store";
import {
  getFormmatedDate,
  getMonthAndYear
} from "../../../helpers/et3-date-helper";
import { apiCallSaveSupervisorIssues } from "../../../api/supervisor-api/api-call-save-supervisor-issues";
import { isPositiveNumber, reduce } from "et3-type-guards";
import { apiCallGetSupervisorReceiptCompensation } from "../../../api/supervisor-api/api-call-get-supervisor-compensation";
import {
  calculateAndUpdateCompensationDiscount,
  getCompensationAuditType
} from "../../../helpers/et3-receipt-compensation-and-calculation/receipt-compensation-calculation";
import { currentMonthSupervisorArray } from "../../../helpers/et3-receipt-compensation-and-calculation/supervisor-receipt-helpers";
import { TEt3PickerSingleValue } from "../../../TypesNEW";
import { reducerToZeroIfUndefined } from "../../../helpers/et3-picker-data-helper";

class SupervisorReceiptStore {
  currentMonthSupervisorReceipt = observable.box<TCurrentMonth>();
  dataInputObject = observable.box<TDataInputObject>();
  configurationReceipt = observable.box<TConfigurationReceipt>(
    CONFIGURATION_RECEIPT_DEFAULTS
  );
  adminFinalReceipts = observable.box<TAdminFinalReceipts>([]);
  submitButtonEnabled = observable.box<boolean>(false);
  csrAdmins = observable.box<TEt3PickerSingleValue>({
    value: "",
    label: i18n.get("CHOOSE")
  });
  tableInputID = observable.box<TReceiptCompensationTitles>("description");

  updateSubmitButtonState = () => {
    const currentDate = new Date();
    const dayOfMonth = currentDate.getDate();
    runInAction(() => {
      this.submitButtonEnabled.set(
        dayOfMonth >= SUPERVISOR_SUBMIT_BUTTON_MIN_DATE_ENABLED &&
          dayOfMonth <= SUPERVISOR_SUBMIT_BUTTON_MAX_DATE_ENABLED
      );
    });
  };

  setCSRAdmins = (admin: TEt3PickerSingleValue) => {
    runInAction(() => {
      this.csrAdmins.set(admin);
    });
  };

  setFinalReceiptDashboard = (data: TAdminFinalReceipts) => {
    runInAction(() => {
      const finalData = data.map((item) => {
        return {
          ...item,
          createdOn: getMonthAndYear(item.createdOn)
        };
      });
      this.adminFinalReceipts.set(finalData);
    });
  };

  setAdminIssues = (newObject: {
    descriptionIssuesCount: number;
    pictureIssuesCount: number;
    reportIssuesCount: number;
    investigationCaseIssuesCount: number;
  }) => {
    runInAction(() => {
      const dataInputObject = this.dataInputObject.get();
      dataInputObject.descriptionIssuesCount = newObject.descriptionIssuesCount;
      dataInputObject.pictureIssuesCount = newObject.pictureIssuesCount;
      dataInputObject.reportIssuesCount = newObject.reportIssuesCount;
      dataInputObject.investigationCaseIssuesCount =
        newObject.investigationCaseIssuesCount;
      this.dataInputObject.set(dataInputObject);
    });
  };

  finalSalary = computed(() => {
    const supervisorCompensation = this.currentMonthSupervisorReceipt.get();
    const calculatedSalary =
      supervisorCompensation.description.price +
      supervisorCompensation.description.discount +
      supervisorCompensation.report.price +
      supervisorCompensation.report.discount +
      supervisorCompensation.picture.price +
      supervisorCompensation.picture.discount +
      supervisorCompensation.investigationCases.price +
      supervisorCompensation.investigationCases.discount;
    return calculatedSalary;
  });

  calculatingCurrentMonth = () => {
    const dataInputObject = this.dataInputObject.get();
    CURRENT_MONTH_CALCULATION_ARRAY.map((objectKey) => {
      const currentMonthReceipt = calculateAndUpdateCompensationDiscount(
        dataInputObject,
        objectKey,
        this.configurationReceipt.get(),
        this.currentMonthSupervisorReceipt.get()
      );
      this.currentMonthSupervisorReceipt.set(currentMonthReceipt);
    });
  };

  setAdminIssuesCounts = (
    eventType: TReceiptCompensationTitles,
    issuesCount: number
  ) => {
    const eventAuditedType = getCompensationAuditType(eventType);
    runInAction(() => {
      const dataInputObject = this.dataInputObject.get();
      Object.keys(dataInputObject).map((key) => {
        if (key === eventAuditedType.issuesCount) {
          if (issuesCount === -1) {
            dataInputObject[eventAuditedType.issuesCount] = 0;
          } else {
            dataInputObject[eventAuditedType.issuesCount] = issuesCount;
          }
          const currentMonthReceipt = calculateAndUpdateCompensationDiscount(
            dataInputObject,
            eventType,
            this.configurationReceipt.get(),
            this.currentMonthSupervisorReceipt.get()
          );
          this.currentMonthSupervisorReceipt.set(currentMonthReceipt);
          return;
        }
      });
      this.setAdminIssues(dataInputObject);
    });
  };

  getSupervisorReceiptCompensation = async () => {
    const adminID = reducerToZeroIfUndefined(
      parseInt(this.csrAdmins.get().value, 10)
    );
    const definedAdminNumber = isPositiveNumber(adminID);
    if (!definedAdminNumber) {
      return;
    }
    getCoreUIStore().setIsLoading("SUPERVISOR_RECEIPT_COMPENSATION", true);
    const { status, data, code } =
      await apiCallGetSupervisorReceiptCompensation(adminID);
    getCoreUIStore().setIsLoading("SUPERVISOR_RECEIPT_COMPENSATION", false);
    const eventsCount = reduce(
      () => data.eventsCount,
      ADMIN_EVENT_COUNTS_DEFAULTS
    );
    const auditedEventsCount = reduce(
      () => data.auditedEventsCount,
      ADMIN_AUDITED_EVENT_COUNTS_DEFAULTS
    );
    const adminIssuesCount = reduce(
      () => data.adminIssuesCount,
      ADMIN_DEFAULT_ISSUES_COUNT
    );
    const configurationReceipt = reduce(
      () => data.configurationReceipt,
      CONFIGURATION_RECEIPT_DEFAULTS
    );
    const adminFinalReceipts = reduce(() => data.adminFinalReceipts, []);
    this.dataInputObject.set({
      ...eventsCount,
      ...auditedEventsCount,
      ...adminIssuesCount
    });
    if (status === "OK") {
      this.configurationReceipt.set(configurationReceipt);
      this.setFinalReceiptDashboard(adminFinalReceipts);
      this.calculatingCurrentMonth();
      getSupervisorReceiptStore().updateSubmitButtonState();
    } else {
      this.adminFinalReceipts.set(adminFinalReceipts);
      this.currentMonthSupervisorReceipt.set(SUPERVISOR_CURRENT_MONTH_DEFAULTS);
      getCorePopUpStore().showAlertMessage(code);
    }
  };

  saveSupervisorIssues = async () => {
    const supervisorIssuesCount = {
      adminID: reducerToZeroIfUndefined(
        parseInt(this.csrAdmins.get().value, 10)
      ),
      descriptionIssuesCount: this.dataInputObject.get().descriptionIssuesCount,
      pictureIssuesCount: this.dataInputObject.get().pictureIssuesCount,
      reportIssuesCount: this.dataInputObject.get().reportIssuesCount,
      investigationCaseIssuesCount:
        this.dataInputObject.get().investigationCaseIssuesCount
    };
    getCoreUIStore().setIsLoading("SUPERVISOR_RECEIPT_COMPENSATION", true);
    const { status, code } = await apiCallSaveSupervisorIssues(
      supervisorIssuesCount
    );
    getCoreUIStore().setIsLoading("SUPERVISOR_RECEIPT_COMPENSATION", false);
    if (status === "OK") {
      getCorePopUpStore().showSuccessMessage(
        "SUPERVISOR_RECEIPT_COMPENSATION_ISSUES_ADDED_SUCCESSFULLY"
      );
      return;
    } else {
      getCorePopUpStore().showAlertMessage(code);
      getCoreUIStore().setIsLoading("SUPERVISOR_RECEIPT_COMPENSATION", false);
    }
  };

  submitSupervisorCurrentMonthReceipt = async () => {
    const [newlyAddedMonth] = currentMonthSupervisorArray();
    getCoreUIStore().setIsLoading("SUPERVISOR_RECEIPT_COMPENSATION", true);
    const { status, code } =
      await apiCallSubmitSupervisorCurrentMonthReceipt(newlyAddedMonth);
    getCoreUIStore().setIsLoading("SUPERVISOR_RECEIPT_COMPENSATION", false);
    if (status === "OK") {
      this.setAdminIssues(ADMIN_DEFAULT_ISSUES_COUNT);
      const newPreviousMonthsData = [...this.adminFinalReceipts.get()];
      newPreviousMonthsData.unshift({
        ...newlyAddedMonth,
        createdOn: getMonthAndYear(`${getFormmatedDate(new Date())}`)
      });
      this.adminFinalReceipts.set(newPreviousMonthsData);
    } else {
      getCorePopUpStore().showAlertMessage(code);
      getCoreUIStore().setIsLoading("SUPERVISOR_RECEIPT_COMPENSATION", false);
    }
  };
}
export const getSupervisorReceiptStore = memoize(
  () => new SupervisorReceiptStore(),
  () => 1
);
export default getSupervisorReceiptStore;
